import * as THREE from 'three'

interface DamageNumber {
  el: HTMLDivElement
  life: number
  x: number
  y: number
}

const LIFE = 0.85 // seconds visible
const RISE = 48 // px the number floats up over its life

/**
 * Floating blue damage numbers shown on the shooter's screen: how much health a
 * hit took off, popped at the hit point (projected to screen) and floating up as
 * it fades. DOM overlay so it stays crisp over the WebGL canvas.
 */
export default class DamageNumbers {
  private readonly root: HTMLDivElement
  private readonly pool: DamageNumber[] = []
  private next = 0
  private readonly ndc = new THREE.Vector3()

  constructor(size = 16) {
    this.root = document.createElement('div')
    this.root.className = 'dmgnums'
    document.body.appendChild(this.root)
    for (let i = 0; i < size; i++) {
      const el = document.createElement('div')
      el.className = 'dmgnum'
      el.style.opacity = '0'
      this.root.appendChild(el)
      this.pool.push({ el, life: 0, x: 0, y: 0 })
    }
  }

  /** Pop `amount` at the world-space `point`, projected through `camera`. */
  public spawn(
    point: THREE.Vector3,
    amount: number,
    camera: THREE.Camera,
    width: number,
    height: number,
  ): void {
    this.ndc.copy(point).project(camera)
    if (this.ndc.z > 1) return // behind the camera

    const d = this.pool[this.next]
    this.next = (this.next + 1) % this.pool.length
    d.x = (this.ndc.x * 0.5 + 0.5) * width + (Math.random() * 22 - 11)
    d.y = (-this.ndc.y * 0.5 + 0.5) * height
    d.el.textContent = `-${amount}`
    d.life = LIFE
    d.el.style.opacity = '1'
    d.el.style.transform = `translate(-50%, -50%) translate(${d.x}px, ${d.y}px)`
  }

  public update(dt: number): void {
    for (const d of this.pool) {
      if (d.life <= 0) continue
      d.life -= dt
      if (d.life <= 0) {
        d.el.style.opacity = '0'
        continue
      }
      const t = 1 - d.life / LIFE // 0 → 1 over its life
      d.el.style.transform = `translate(-50%, -50%) translate(${d.x}px, ${d.y - t * RISE}px)`
      d.el.style.opacity = String(Math.min(1, (1 - t) * 1.8))
    }
  }

  public dispose(): void {
    this.root.remove()
  }
}
